import { buildTitle } from "@/lib/seo-quality";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Phone, ArrowRight, ArrowLeft, CheckCircle2, AlertCircle, MapPin } from "lucide-react";
import { PageHeader } from "./fordon";
import type { RapidoModel } from "@/data/rapido-models";
import { getRapidoModel, RAPIDO_MODELS } from "@/data/rapido-models";

export const Route = createFileRoute("/rapido/modeller/$model")({
  codeSplitGroupings: [["loader", "component"]],
  loader: ({ params }) => {
    const model = getRapidoModel(params.model);
    if (!model) throw notFound();
    return { model };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const m = loaderData.model;
    const url = `https://www.akhusbilfritid.se/rapido/modeller/${m.slug}`;
    return {
      meta: [
        { title: buildTitle(m.metaTitle) },
        { name: "description", content: m.metaDesc },
        { name: "keywords", content: `${m.keyword}, ${m.name.toLowerCase()}, ${m.name.toLowerCase()} pris, ${m.name.toLowerCase()} specifikationer, rapido husbil` },
        { property: "og:type", content: "website" },
        { property: "og:title", content: m.metaTitle },
        { property: "og:description", content: m.metaDesc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: m.name,
            brand: { "@type": "Brand", name: "Rapido" },
            category: m.category,
            description: m.intro,
            offers: {
              "@type": "AggregateOffer",
              priceCurrency: "SEK",
              lowPrice: m.priceUsed.replace(/\D/g, "").slice(0, 6),
              availability: "https://schema.org/InStock",
              seller: { "@type": "AutoDealer", name: "AK Husbil & Fritid" },
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: m.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
              { "@type": "ListItem", position: 2, name: "Rapido", item: "https://www.akhusbilfritid.se/rapido" },
              { "@type": "ListItem", position: 3, name: m.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: ModelPage,
  errorComponent: ({ error }: { error: unknown }) => (
    <div className="container-edge py-20" role="alert">Något gick fel: {error instanceof Error ? error.message : String(error)}</div>
  ),
  notFoundComponent: () => (
    <div className="container-edge py-20 text-center">
      <h1 className="font-display text-3xl">Modellen finns inte</h1>
      <p className="mt-4 text-muted-foreground">Prova att gå tillbaka till modellöversikten.</p>
      <Link to="/rapido" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-accent">
        <ArrowLeft size={14} /> Till Rapido-översikten
      </Link>
    </div>
  ),
});

function ModelPage() {
  const { model: m } = Route.useLoaderData() as { model: RapidoModel };

  const specRows: [string, string][] = [
    ["Kategori", m.category],
    ["Chassi", m.chassis],
    ["Längd", `${m.lengthCm} cm`],
    ["Bredd", `${m.widthCm} cm`],
    ["Höjd", `${m.heightCm} cm`],
    ["Totalvikt", `${m.totalWeightKg} kg`],
    ["Sovplatser", m.sleeps],
    ["Bältade platser", m.belts],
    ["Sängar", m.beds],
    ["Kök", m.kitchen],
    ["Våtrum", m.bathroom],
    ["Värme", m.heating],
    ["Färskvatten", m.fresh],
    ["Gråvatten", m.waste],
    ["Batteri", m.battery],
    ["Garage/förvaring", m.garage],
    ["Nypris (från)", m.priceNew],
    ["Begagnat prisintervall", m.priceUsed],
  ];

  const otherModels = RAPIDO_MODELS.filter((x) => x.slug !== m.slug).slice(0, 3);

  return (
    <>
      <PageHeader
        label={`Rapido · ${m.category}`}
        title={<>{m.h1}</>}
        sub={m.intro}
      />

      <section className="container-edge -mt-6 pb-4">
        <div className="flex flex-wrap gap-3">
          <Link to="/rapido/begagnade" className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90">
            Se {m.name} i lager <ArrowRight size={14} />
          </Link>
          <a href="tel:0708629743" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            <Phone size={16} /> 070-862 97 43
          </a>
          <Link to="/kontakt" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
            <MapPin size={16} /> Boka besök i Kalix
          </Link>
        </div>
      </section>

      {/* Snabbfakta + spec */}
      <section className="container-edge py-16 md:py-20">
        <div className="grid gap-10 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <p className="eyebrow">Snabbfakta</p>
            <h2 className="mt-3 font-display text-4xl">{m.name} — <span className="font-italic-display text-accent">för vem</span>?</h2>
            <ul className="mt-8 space-y-3">
              {m.perfectFor.map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 shrink-0 text-accent" size={20} />
                  <span className="text-foreground/85">{t}</span>
                </li>
              ))}
            </ul>

            <div className="mt-12 grid gap-6 md:grid-cols-2">
              <div className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-display text-xl">Styrkor</h3>
                <ul className="mt-4 space-y-2 text-sm text-foreground/85">
                  {m.strengths.map((s) => (
                    <li key={s} className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 shrink-0 text-accent" size={16} />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-display text-xl">Att tänka på</h3>
                <ul className="mt-4 space-y-2 text-sm text-foreground/85">
                  {m.weaknesses.map((s) => (
                    <li key={s} className="flex items-start gap-2">
                      <AlertCircle className="mt-0.5 shrink-0 text-accent" size={16} />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="rounded-3xl border border-border bg-card p-6 md:p-8">
              <h3 className="font-display text-2xl">Specifikationer</h3>
              <dl className="mt-6 divide-y divide-border text-sm">
                {specRows.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 py-3">
                    <dt className="text-muted-foreground">{k}</dt>
                    <dd className="text-right font-medium">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-warm">
        <div className="container-edge py-16 md:py-24">
          <p className="eyebrow">Vanliga frågor om {m.name}</p>
          <h2 className="mt-3 font-display text-4xl md:text-5xl">Om <span className="font-italic-display text-accent">{m.name}</span>.</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {m.faq.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-display text-xl">{f.q}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Andra modeller */}
      <section className="container-edge py-16 md:py-20">
        <p className="eyebrow">Fler modeller</p>
        <h2 className="mt-3 font-display text-4xl">Andra <span className="font-italic-display text-accent">Rapido-modeller</span>.</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {otherModels.map((o) => (
            <Link
              key={o.slug}
              to="/rapido/modeller/$model"
              params={{ model: o.slug }}
              className="group rounded-3xl border border-border bg-card p-6 transition hover:-translate-y-1 hover:shadow-xl"
            >
              <p className="eyebrow">{o.category}</p>
              <h3 className="mt-3 font-display text-2xl">{o.name}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{o.intro.slice(0, 100)}…</p>
              <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-accent">
                Läs mer <ArrowRight size={14} className="transition group-hover:translate-x-0.5" />
              </span>
            </Link>
          ))}
        </div>
        <div className="mt-8">
          <Link to="/rapido" className="inline-flex items-center gap-1 text-sm font-semibold text-accent">
            <ArrowLeft size={14} /> Alla Rapido-modeller
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="container-edge pb-20">
        <div className="rounded-3xl border border-border bg-foreground p-8 md:p-14 text-background">
          <p className="eyebrow" style={{ color: "hsl(var(--accent))" }}>Nästa steg</p>
          <h2 className="mt-3 font-display text-4xl md:text-6xl leading-[1.05]">Prova en {m.name}.</h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed opacity-80">
            Boka en tid i Kalix — vi går igenom modellen, du sätter dig i den, och vi räknar konkret på ditt behov (nytt eller begagnat, inbyte, finansiering).
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="tel:0708629743" className="inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3.5 text-sm font-semibold text-accent-foreground hover:opacity-90">
              <Phone size={16} /> 070-862 97 43
            </a>
            <Link to="/kontakt" className="inline-flex items-center gap-2 rounded-full border border-background/30 px-6 py-3.5 text-sm font-semibold hover:bg-background/10">
              <MapPin size={16} /> Boka besök
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
